import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { filter, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CustomFilterService } from '../../../../../core/services/custom-filter.service';
import { IDropdownOption } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../utilities/http-utilities.service';
import { IWidgetRequest } from '../../../data-integrations/widget-requests/widget-requests.interface';
import { ICustomFilter, ICustomFilterAddPayload, ICustomFilterEditPayload } from '../custom-filters.interface';

export interface ICustomFiltersAddEditDialogState {
  inUseData: [string, string][];
  inUseDataLoadStatus: ELoadStatus;
  widgetRequests: IDropdownOption[];
  widgetRequestsStatus: ELoadStatus;
  createUpdateStatus: ELoadStatus;
}

@Injectable()
export class CustomFiltersAddEditDialogStore extends ComponentStore<ICustomFiltersAddEditDialogState> {
  private readonly httpUtilities = inject(HttpUtilitiesService);
  private readonly http = inject(HttpClient);
  private readonly customFilterService = inject(CustomFilterService);

  public readonly widgetRequests = this.selectSignal(({ widgetRequests }) => widgetRequests);
  public readonly inUseData = this.selectSignal(({ inUseData }) => inUseData);
  public readonly afterCreateUpdateSuccess$ = this.select(({ createUpdateStatus }) => createUpdateStatus).pipe(
    filter((status) => status === ELoadStatus.success),
  );

  readonly loadWidgetRequests = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ widgetRequestsStatus: ELoadStatus.loading });

        return this.getWidgetRequests(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                widgetRequests: response.data.map(({ id, name }) => ({ id, name })),
                widgetRequestsStatus: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadInUseData = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id) => {
        this.patchState({ inUseDataLoadStatus: ELoadStatus.loading });

        return this.customFilterService.getInUseData(id).pipe(
          tapResponse(
            ({ data }) =>
              this.patchState({
                inUseData: Object.entries(data).map(([errorKey, context]) => [errorKey, context.join(', ')]),
                inUseDataLoadStatus: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly createOne = this.effect((trigger$: Observable<ICustomFilterAddPayload>) =>
    trigger$.pipe(
      switchMap((item) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ createUpdateStatus: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<ICustomFilterEditPayload>) =>
    trigger$.pipe(
      switchMap((item) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ createUpdateStatus: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor() {
    super({
      createUpdateStatus: ELoadStatus.initial,
      inUseData: [],
      inUseDataLoadStatus: ELoadStatus.initial,
      widgetRequests: [],
      widgetRequestsStatus: ELoadStatus.initial,
    });
  }

  private getWidgetRequests(params: HttpParams): Observable<IGetManyResponse<IWidgetRequest>> {
    return this.http.get<IGetManyResponse<IWidgetRequest>>('widget-requests', { params });
  }

  private createItem(body: ICustomFilterAddPayload): Observable<IBaseResponse<ICustomFilter>> {
    return this.http.post<IBaseResponse<ICustomFilter>>('custom-filters', body);
  }

  private updateItem(edit: ICustomFilterEditPayload): Observable<IBaseResponse<ICustomFilter>> {
    return this.http.patch<IBaseResponse<ICustomFilter>>(`custom-filters/${edit.id}`, edit.dto);
  }
}
